import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import Authorization from 'components/global/Authorization';

import { ENUMS } from 'services/consts';

import AdvanceSettings from './AdvanceSettings';
import BaseUrl from './Fields/BaseUrl';
import StoreCredentials from './Fields/StoreCredentials';

const DeveloperSettings = ({ statusesEnum, fields = [] }) => {
	const {
		watch,
		setValue,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = useFormContext();

	const isLoading = isSubmitting || isFormLoading;
	const { t } = useTranslation();
	return (
		<Authorization path="SalesChannelFormSupportSettings">
			<AdvanceSettings developerSettings>
				{fields.includes('secret') && (
					<FormItem label="secret" isLoading={isLoading}>
						<StringInput name="secret" placeholder={t('fieldPlaceholder', { name: t(`secret`) })} />
					</FormItem>
				)}
				{fields.includes('apiUrl') && (
					<FormItem label="apiUrl" isLoading={isLoading}>
						<StringInput name="apiUrl" placeholder={t('fieldPlaceholder', { name: t(`apiUrl`) })} />
					</FormItem>
				)}
				{fields.includes('baseUrl') && <BaseUrl />}
				{fields.includes('productUrl') && (
					<FormItem label="productUrl" isLoading={isLoading}>
						<StringInput name="productUrl" placeholder={t('fieldPlaceholder', { name: t(`productUrl`) })} />
					</FormItem>
				)}
				{fields.includes('password') && fields.includes('username') && (
					<StoreCredentials isLoading={isLoading} />
				)}
				{fields.includes('token') && (
					<FormItem label="token" isLoading={isLoading}>
						<StringInput name="token" placeholder={t('fieldPlaceholder', { name: t(`token`) })} />
					</FormItem>
				)}
				{fields.includes('codString') && (
					<FormItem label="codString" isLoading={isLoading}>
						<StringInput name="codString" placeholder={t('fieldPlaceholder', { name: t(`codString`) })} />
					</FormItem>
				)}
				{fields.includes('commentKeys') && (
					<FormItem label="commentKeys" isLoading={isLoading}>
						<StringInput
							name="commentKeys"
							placeholder={t('fieldPlaceholder', { name: t(`commentKeys`) })}
						/>
					</FormItem>
				)}
				{fields.includes('ignoreNonCodOnPending') && (
					<FormItem label="ignoreNonCodOnPending" isLoading={isLoading}>
						<StringInput
							name="ignoreNonCodOnPending"
							placeholder={t('fieldPlaceholder', { name: t(`ignoreNonCodOnPending`) })}
						/>
					</FormItem>
				)}
				{fields.includes('lastUpdateDate') && (
					<FormItem label="lastUpdateDate" isLoading={isLoading}>
						<DateInput
							name="lastUpdateDate"
							value={watch('lastUpdateDate')}
							setValue={(value) => {
								setValue('lastUpdateDate', value, { shouldValidate: true, shouldDirty: true });
							}}
							placeholder={t('fieldPlaceholder', { name: t(`lastUpdateDate`) })}
						/>
					</FormItem>
				)}
				{fields.includes('webhookStatus') && (
					<FormItem label="webhookStatus" isLoading={isLoading}>
						<SwitchInput
							formMode
							name="webhookStatus"
							placeholder={t('fieldPlaceholder', { name: t(`webhookStatus`) })}
						/>
					</FormItem>
				)}
				{fields.includes('productUpdateDate') && (
					<FormItem label="productUpdateDate" isLoading={isLoading}>
						<DateInput
							name="productUpdateDate"
							value={watch('productUpdateDate')}
							setValue={(value) => {
								setValue('productUpdateDate', value, { shouldValidate: true, shouldDirty: true });
							}}
							placeholder={t('fieldPlaceholder', { name: t(`productUpdateDate`) })}
						/>
					</FormItem>
				)}
				{fields.includes('trackingUrlType') && (
					<FormItem label="trackingUrlType" isLoading={isLoading}>
						<EnumSelect
							searchable={false}
							name="TrackingUrlType"
							enumName={ENUMS.TRACKING_URL_TYPE}
							setValue={(value) => {
								setValue(`TrackingUrlType`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`TrackingUrlType`)}
							placeholder={t('fieldPlaceholder', { name: t(`TrackingUrlType`) })}
						/>
					</FormItem>
				)}
				{fields.includes('filterByStatuses') && (
					<FormItem label="filterByStatuses" isLoading={isLoading}>
						<EnumSelect
							searchable={false}
							name="statuses"
							multipleSelection
							enumName={statusesEnum}
							setValue={(value) => {
								setValue(`statuses`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`statuses`)}
							placeholder={t('fieldPlaceholder', { name: t(`filterByStatuses`) })}
						/>
					</FormItem>
				)}

				{/* Common Fields - These will always render */}
				<FormItem label="ignoreRecentOrders" isLoading={isLoading}>
					<IntegerInput
						name="ignoreRecentOrders"
						unit="seconds"
						placeholder={t('fieldPlaceholder', { name: t(`ignoreRecentOrders`) })}
					/>
				</FormItem>
				<FormItem label="excludePaymentMethods" isLoading={isLoading}>
					<StringInput
						name="excludePaymentMethods"
						placeholder={t('fieldPlaceholder', { name: t(`excludePaymentMethods`) })}
					/>
				</FormItem>
				<FormItem label="fullyPaid" isLoading={isLoading}>
					<StringInput name="fullyPaid" placeholder={t('fieldPlaceholder', { name: t(`fullyPaid`) })} />
				</FormItem>
				<FormItem label="fullyNOTPaid" isLoading={isLoading}>
					<StringInput name="fullyNOTPaid" placeholder={t('fieldPlaceholder', { name: t(`fullyNOTPaid`) })} />
				</FormItem>
				<FormItem label="statusNeedConfirm" isLoading={isLoading}>
					<StringInput
						name="statusNeedConfirm"
						placeholder={t('fieldPlaceholder', { name: t(`statusNeedConfirm`) })}
					/>
				</FormItem>
				<FormItem label="statusOnhold" isLoading={isLoading}>
					<StringInput name="statusOnhold" placeholder={t('fieldPlaceholder', { name: t(`statusOnhold`) })} />
				</FormItem>
				<FormItem label="cityLabel" isLoading={isLoading}>
					<StringInput name="cityLabel" placeholder={t('fieldPlaceholder', { name: t(`cityLabel`) })} />
				</FormItem>
				<FormItem label="districtLabel" isLoading={isLoading}>
					<StringInput
						name="districtLabel"
						placeholder={t('fieldPlaceholder', { name: t(`districtLabel`) })}
					/>
				</FormItem>
				<FormItem label="assignToWarehouse" isLoading={isLoading}>
					<SwitchInput
						formMode
						name="assignToWarehouse"
						placeholder={t('fieldPlaceholder', { name: t(`assignToWarehouse`) })}
					/>
				</FormItem>
				<FormItem label="metadata" isLoading={isLoading}>
					<TextArea
						name="metadata"
						placeholder={t('fieldPlaceholder', { name: t(`metadata`) })}
						maxlength={12096}
					/>
				</FormItem>
			</AdvanceSettings>
		</Authorization>
	);
};

export default DeveloperSettings;
